import { ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { logout } from '../redux/slices/authSlice';
import { Chalkboard, Clock, LockKey, Megaphone, SignOut, List, X } from 'phosphor-react';
import AmbientBackground from './AmbientBackground';

const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
  [
    'group relative flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition-all duration-200',
    isActive
      ? 'border border-brand-400/30 bg-brand-500/15 text-white shadow-glow'
      : 'border border-transparent text-white/60 hover:border-white/10 hover:bg-white/5 hover:text-white',
  ].join(' ');

const NavItems = ({ isAdmin, onNavigate }: { isAdmin: boolean; onNavigate?: () => void }) => (
  <>
    <NavLink to="/" end className={navLinkClasses} onClick={onNavigate}>
      {({ isActive }) => (
        <>
          <span
            className={`absolute left-0 top-1/2 h-6 -translate-y-1/2 rounded-r-full bg-brand-400 transition-all duration-200 ${
              isActive ? 'w-1 opacity-100' : 'w-0 opacity-0'
            }`}
          />
          <Chalkboard size={18} weight={isActive ? 'fill' : 'regular'} />
          <span>Dashboard</span>
        </>
      )}
    </NavLink>

    <NavLink to="/complaints" className={navLinkClasses} onClick={onNavigate}>
      {({ isActive }) => (
        <>
          <span
            className={`absolute left-0 top-1/2 h-6 -translate-y-1/2 rounded-r-full bg-brand-400 transition-all duration-200 ${
              isActive ? 'w-1 opacity-100' : 'w-0 opacity-0'
            }`}
          />
          <Megaphone size={18} weight={isActive ? 'fill' : 'regular'} />
          <span>Complaints</span>
        </>
      )}
    </NavLink>

    {isAdmin ? (
      <NavLink to="/admin" className={navLinkClasses} onClick={onNavigate}>
        {({ isActive }) => (
          <>
            <span
              className={`absolute left-0 top-1/2 h-6 -translate-y-1/2 rounded-r-full bg-brand-400 transition-all duration-200 ${
                isActive ? 'w-1 opacity-100' : 'w-0 opacity-0'
              }`}
            />
            <LockKey size={18} weight={isActive ? 'fill' : 'regular'} />
            <span>Admin</span>
          </>
        )}
      </NavLink>
    ) : null}
  </>
);

const UserCard = ({ name, email }: { name?: string; email?: string }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-3">
    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-400/30 to-brand-600/20 text-sm font-semibold text-brand-100 ring-1 ring-brand-400/20">
      {name?.charAt(0)?.toUpperCase() ?? '?'}
    </div>
    <div className="min-w-0">
      <div className="truncate text-sm font-medium text-white">{name}</div>
      <div className="truncate text-xs text-white/45">{email}</div>
    </div>
  </div>
);

const Logo = () => (
  <Link to="/" className="flex w-fit items-center gap-3">
    <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-brand-400/30 bg-white/10 text-white shadow-glow backdrop-blur-2xl">
      <Clock size={22} weight="bold" className="text-brand-200" />
    </div>
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-200/90">VeraConnect</div>
      <div className="text-lg font-semibold leading-tight text-white">Work Hours</div>
    </div>
  </Link>
);

interface ShellProps {
  children: ReactNode;
}

const Shell = ({ children }: ShellProps) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = Boolean(user && user.hrDashboardAdmin);
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  return (
    <div className="relative flex min-h-screen w-full flex-col text-white lg:flex-row lg:gap-4 lg:p-4">
      <AmbientBackground />

      {/* Desktop sidebar */}
      <aside className="z-30 hidden flex-shrink-0 flex-col px-2 py-4 lg:sticky lg:top-4 lg:flex lg:h-[calc(100vh-32px)] lg:w-[280px]">
        <Logo />

        <nav className="mt-6 flex w-full flex-col gap-1.5 border-t border-white/10 pt-6">
          <NavItems isAdmin={isAdmin} />
        </nav>

        <div className="mt-auto flex w-full flex-col gap-3 border-t border-white/10 pt-4">
          <UserCard name={user?.name} email={user?.email} />
          <button
            type="button"
            onClick={() => void dispatch(logout())}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white/80 transition hover:bg-white/10 hover:text-white"
          >
            <SignOut size={16} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Mobile slide-out drawer */}
      {mobileNavOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileNavOpen(false)}
          />
          <div className="absolute left-0 top-0 flex h-full w-[280px] max-w-[82%] flex-col border-r border-white/10 bg-[#0b1d10]/95 px-4 py-5 shadow-glow backdrop-blur-2xl">
            <div className="flex items-center justify-between">
              <Logo />
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/70 hover:text-white"
                aria-label="Close navigation"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="mt-6 flex w-full flex-col gap-1.5 border-t border-white/10 pt-6">
              <NavItems isAdmin={isAdmin} onNavigate={() => setMobileNavOpen(false)} />
            </nav>
            <div className="mt-auto flex w-full flex-col gap-3 border-t border-white/10 pt-4">
              <UserCard name={user?.name} email={user?.email} />
              <button
                type="button"
                onClick={() => void dispatch(logout())}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white/80 transition hover:bg-white/10 hover:text-white"
              >
                <SignOut size={16} />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <main className="flex min-h-[calc(100vh-72px)] w-full flex-col rounded-t-[24px] border border-white/10 bg-[#0d1a12]/80 px-4 pb-6 text-white shadow-glow backdrop-blur-xl lg:rounded-[24px] lg:px-8">
        {/* Mobile top bar */}
        <div className="sticky top-0 z-40 mb-4 flex w-full items-center justify-between border-b border-white/10 bg-[#0d1a12]/85 py-4 backdrop-blur-xl lg:hidden">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/80 transition hover:bg-white/10 hover:text-white"
              aria-label="Open navigation"
            >
              <List size={18} />
            </button>
            <div className="text-sm font-semibold uppercase tracking-[0.28em] text-brand-200/90">VeraConnect HR</div>
          </div>
          <button
            type="button"
            onClick={() => void dispatch(logout())}
            className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white/80 transition hover:bg-white/10"
          >
            <SignOut size={14} />
            Sign out
          </button>
        </div>
        <div className="flex h-full w-full flex-1 flex-col py-6">{children}</div>
      </main>
    </div>
  );
};

export default Shell;